import { FC, useEffect, useRef, useState } from "react"
import {
  generatePath,
  matchPath,
  Outlet,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom"

import { ULoading } from "../components"
import { useAppDispatch, useAppSelector } from "../core/app/hooks"
import { getCaseById } from "../core/app/slices/case/caseThunkApi"
import { getQuotation } from "../core/app/slices/order/orderThunkApi"
import { fetchPatientById } from "../core/app/slices/patients/patientThunkApi"
import { updateCurrentOrgId } from "../core/app/slices/user/userSlice"
import { RootState } from "../core/app/store"
import { useMyNavigation } from "../hooks/useMyNavigation"
import { getQuoteRequestObj } from "../modules/Patient/PatientList/util/commonUtil"

import {
  ROUTE_BY_MODULE,
  shouldNavigateToPatients,
  verifyRoutAllowedByStatus,
} from "./status.route.config"
import { updateNavigateToPatients } from "@/core/app/slices/order"

const RouteByStatus: FC = () => {
  const location = useLocation()
  const navigate = useMyNavigation()
  const [loading, setLoading] = useState<boolean>(true)
  const { patientId, caseId, orgId } = useParams()
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const isRetainerFastrack = searchParams.get("isRetainerFastrack")

  const { caseDetail } = useAppSelector((state: RootState) => state.caseService)
  const { user } = useAppSelector((state: RootState) => state.userService)
  const { patientData } = useAppSelector(
    (state: RootState) => state.patientService,
  )
  const { selectedBundle, navigateToPatients } = useAppSelector(
    (state: RootState) => state.orderService,
  )
  const { rxPrescription } = useAppSelector(
    (state: RootState) => state.formService,
  )

  // Track if quotation has been called to prevent duplicate calls
  const hasCalledQuotationRef = useRef(false)

  const callGetQuotation = () => {
    const requestObj = getQuoteRequestObj(caseDetail, )
    const isOrderRetainerFinalAlignerFromOverview =
      searchParams.get("from") === "overview" &&
      searchParams.get("isRetainerFinalAligner") === "true" &&
      !!matchPath(
        `/order/patient/:patientId/case/:caseId/bundle`,
        location.pathname,
      )

    dispatch(
      getQuotation({
        payload: {
          ...requestObj,
          ...((isRetainerFastrack === "true" ||
            isOrderRetainerFinalAlignerFromOverview) && {
            is_retainer_fast_track: true,
          }),
        },
      }),
    )
  }

  useEffect(() => {
    if (!orgId) return
    dispatch(updateCurrentOrgId(orgId))
  }, [dispatch, orgId])

  const checkPermissionAndNavigate = (res) => {
    if (
      !matchPath(
        user.isAllowToViewAllOrg
          ? "org/:orgId/" + ROUTE_BY_MODULE.clinicalPath
          : ROUTE_BY_MODULE.clinicalPath,
        location.pathname,
      ) &&
      res?.payload?.caseDisposition
    ) {
      const x = verifyRoutAllowedByStatus(
        res?.payload?.caseDisposition,
        location.pathname,
        user.isAllowToViewAllOrg,
      )
      if (!x) {
        if (
          shouldNavigateToPatients(
            location.pathname,
            res?.payload?.caseDisposition,
          )
        ) {
          if (matchPath("order/patient/:patientId/case/:caseId/*", location.pathname)) {
            dispatch(updateNavigateToPatients(true))
            return
          }
          navigate("/patients")
          return
        }
        setLoading(false)
        navigate(-1)
      }
    }
  }

  // route permission based on  caseDetail?.caseDisposition
  useEffect(() => {
    if (patientId && (!patientData.id || patientData.id != patientId)) {
      dispatch(
        fetchPatientById({
          patientId,
        }),
      )
    }
    if (caseId && patientId) {
      
      const caseAlreadyExists =
        Object.keys(caseDetail).length &&
        caseDetail.id == Number(caseId) &&
        patientData.id == patientId

      if ( caseAlreadyExists ) {
        checkPermissionAndNavigate(caseDetail)
      } else {
        dispatch(getCaseById({ patientId, caseId })).then((res) =>
          checkPermissionAndNavigate(res),
        )
      }
    }

    setLoading(false)
  }, [location.pathname, caseDetail, patientId])

  //Order Module
  /**
   * if selected bundle not present we should redirect to /bundle selection stage
   * NOTE: If we are in ordering process after bundle selection and user refreshes page,
   * we return to bundle page and fetch quotation
   */
  useEffect(() => {
    // Early returns for invalid states
    if (!caseDetail?.id) {
      setLoading(false)
      return
    }

    // Check if we're on an order route
    if (!matchPath(ROUTE_BY_MODULE.order, location.pathname)) {
      setLoading(false)
      // Reset flag when leaving order routes
      hasCalledQuotationRef.current = false
      return
    }

    const isMatchBundle = matchPath(
      ROUTE_BY_MODULE.orderBundle,
      location.pathname,
    )

    const needsBundle = !selectedBundle || !selectedBundle.sku_name
    // shouldNavigateToPatients is used to redirect user to patient list
    // when user is not on right case disposition to avail ordering
    const shouldNotNavigateToPatients = !shouldNavigateToPatients(
      location.pathname,
      caseDetail.caseDisposition
    )

    // Handle bundle requirement
    if (needsBundle && shouldNotNavigateToPatients) {
      if (isMatchBundle) {
        // Already on bundle page, fetch quotation only once
        if (!hasCalledQuotationRef.current) {
          callGetQuotation()
          hasCalledQuotationRef.current = true
        }
      } else {
        // Need to navigate to bundle page
        const newPath = generatePath(ROUTE_BY_MODULE.orderBundle, {
          patientId,
          caseId,
        })
        const searchParams = location.search
        // Reset flag before navigation so quotation will be called after navigation
        hasCalledQuotationRef.current = false
        // Don't call getQuotation here - it will be called after navigation
        navigate(newPath + searchParams)
        // Don't call setLoading(false) here, let next render handle it
        return
      }
    } else {
      // Reset flag when bundle is selected or conditions change
      hasCalledQuotationRef.current = false
    }

    setLoading(false)
  }, [
    selectedBundle,
    location.pathname,
    caseDetail,
    patientId,
    caseId,
    navigate,
    dispatch,
  ])

  if (loading) {
    return <ULoading isLoading={true} />
  }

  return <Outlet />
}

export default RouteByStatus
